import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Label } from "semantic-ui-react";

import { CustomerModal } from "components";
import { CUSTOMER_TYPES, DESK_COMMUNICATION_EVENTS, KIOSK_COMMUNICATION_EVENT_TYPES } from "models";
import "modules/AppointmentDetails/components/CarDetails/components/CustomerInfo/CustomerInfo.scss";
import { CommunicationLogs as DeskCommunicationLog } from "modules/AppointmentDetails/components/DeskCommunicationLog/components";
import { CommunicationLogs as KioskCommunicationLog } from "modules/AppointmentDetails/components/KioskCommunicationLog/components";
import { useAppointmentDealer, useAppointmentLocation, useCarBrandsQuery } from "modules/AppointmentDetails/hooks";
import { isAppointmentWarrantyPinSignatureMissing } from "utils/appointmentUtils";
import { getCustomerName } from "utils/common";

import type { TFunction } from "i18next";
import type { Appointment, Brand, DealerLocation, DeskCommunication, KioskCommunication } from "models";

type CustomerInfoProps = {
  appointment: Appointment;
  customerType: CUSTOMER_TYPES;
};

const enum COMMUNICATION_LOG_TYPES {
  Desk,
  Kiosk
}

const getCommLogType = (deskComm: DeskCommunication | undefined, kioskComm: KioskCommunication | undefined) => {
  if (!deskComm && !kioskComm) return;
  if (deskComm && !kioskComm) return COMMUNICATION_LOG_TYPES.Desk;
  if (!deskComm && kioskComm) return COMMUNICATION_LOG_TYPES.Kiosk;
  if (deskComm && kioskComm) {
    if (deskComm.created_on > kioskComm.created_on) return COMMUNICATION_LOG_TYPES.Desk;
    else return COMMUNICATION_LOG_TYPES.Kiosk;
  }
};

const getCustomerTypeToHighlight = (appointment: Appointment) => {
  if (appointment.customer_driver) return CUSTOMER_TYPES.Driver;
  if (appointment.customer_contract) return CUSTOMER_TYPES.Contractor;
  if (appointment.customer_owner) return CUSTOMER_TYPES.Owner;
};

const getCustomerSignatureIndicator = (appointment: Appointment, location: DealerLocation, brands: Brand[], customerType: CUSTOMER_TYPES) => {
  const isWarrantyPinSigantureMissing = isAppointmentWarrantyPinSignatureMissing(appointment, brands, location);
  const isCheckinCommAnswered = appointment.check_in_com_answered;

  if (customerType !== getCustomerTypeToHighlight(appointment)) return "";
  if (isWarrantyPinSigantureMissing) return "warranty-signature-missing";
  if (isCheckinCommAnswered) return "customer-checked-in";
};

const getCommLogDefaultActiveIndex = (appointment: Appointment, commLogToOpen: COMMUNICATION_LOG_TYPES | undefined) => {
  if (appointment.desk_communications?.length && commLogToOpen === COMMUNICATION_LOG_TYPES.Desk) return appointment.desk_communications.length - 1;
  if (appointment.kiosk_communications?.length && commLogToOpen === COMMUNICATION_LOG_TYPES.Kiosk) return appointment.kiosk_communications.length - 1;
};

const shouldDisplaySignatureLabel = (appointment: Appointment, customerType: CUSTOMER_TYPES) => {
  if (!appointment.check_in_com_answered || customerType !== getCustomerTypeToHighlight(appointment)) return false;

  const hasDeskAnsweredEvent = appointment.desk_communications?.some(dc => dc.events?.some(event => event.type === DESK_COMMUNICATION_EVENTS.DeskCheckInAnswered));
  const hasKioskAnsweredEvent = appointment.kiosk_communications?.some(kc => kc.events?.some(event => event.type === KIOSK_COMMUNICATION_EVENT_TYPES.CheckInAnswered));

  return hasDeskAnsweredEvent || hasKioskAnsweredEvent;
};

const getCustomerDetails = (appointment: Appointment, customerType: CUSTOMER_TYPES, t: TFunction) => {
  if (customerType === CUSTOMER_TYPES.Driver) {
    return {
      title: t("v8_driver", "Driver"),
      customerId: appointment.customer_driver_id,
      customer: appointment.customer_driver,
      customerOptedOutFromImporting: appointment.driver_opted_out_from_importing
    };
  }

  if (customerType === CUSTOMER_TYPES.Owner) {
    return {
      title: t("v8_owner", "Owner"),
      customerId: appointment.customer_owner_id,
      customer: appointment.customer_owner,
      customerOptedOutFromImporting: appointment.owner_opted_out_from_importing
    };
  }

  if (customerType === CUSTOMER_TYPES.Contractor) {
    return {
      title: t("v8_contractor", "Contractor"),
      customerId: appointment.customer_contract_id,
      customer: appointment.customer_contract,
      customerOptedOutFromImporting: appointment.contractor_opted_out_from_importing
    };
  }
};

export const CustomerInfo = ({ appointment, customerType }: CustomerInfoProps) => {
  const { t } = useTranslation();
  const location = useAppointmentLocation(appointment);
  const dealer = useAppointmentDealer(appointment);
  const { data: brands = [] } = useCarBrandsQuery();
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [commLogToOpen, setCommLogToOpen] = useState<COMMUNICATION_LOG_TYPES | undefined>(undefined);

  const handleOpenCommLog = () => {
    setCommLogToOpen(getCommLogType(appointment.desk_communications?.at(0), appointment.kiosk_communications?.at(0)));
  };

  const handleCommLogClose = () => setCommLogToOpen(undefined);

  const customerDetails = getCustomerDetails(appointment, customerType, t);
  if (!location || !customerDetails) return null;

  const { customerId, title, customer, customerOptedOutFromImporting } = customerDetails;
  const customerName = getCustomerName({ appointment, customerType, showTitle: true, t: t });

  if (!customerOptedOutFromImporting && (!customerId || !customerName || !customer)) return null;

  return (
    <>
      {title}

      {shouldDisplaySignatureLabel(appointment, customerType) && (
        <Label basic onClick={handleOpenCommLog} className="CustomerInfo">
          <Icon className="signature" color="blue" />
        </Label>
      )}

      <div
        onClick={() => {
          if (customerOptedOutFromImporting) return;
          setShowCustomerModal(true);
        }}
        className={getCustomerSignatureIndicator(appointment, location, brands, customerType)}
      >
        {customerName}
      </div>

      <DeskCommunicationLog
        appointment={appointment}
        isModalOpen={commLogToOpen === COMMUNICATION_LOG_TYPES.Desk}
        onToggleIsModalOpen={handleCommLogClose}
        defaultTabActiveIndex={getCommLogDefaultActiveIndex(appointment, commLogToOpen)}
      />
      <KioskCommunicationLog
        appointment={appointment}
        isModalOpen={commLogToOpen === COMMUNICATION_LOG_TYPES.Kiosk}
        onToggleIsModalOpen={handleCommLogClose}
        defaultTabActiveIndex={getCommLogDefaultActiveIndex(appointment, commLogToOpen)}
      />
      {showCustomerModal && customer && (
        <CustomerModal isOpen={showCustomerModal} onClose={() => setShowCustomerModal(false)} customer={customer} dealer={dealer} location={location} />
      )}
    </>
  );
};
